import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import JournalEditor from "./JournalEditor";

const EMPTY_DOC = { type: "doc", content: [{ type: "paragraph" }] };
export const isEmptyDoc = (doc) => !doc?.content?.some((n) => (n.content && n.content.length) || n.type === "mention");
/* Pre-filled doc for "Add to journal": the quote as a blockquote + a mention chip for its source. */
const seedDoc = (seed) => ({ type: "doc", content: [
  { type: "blockquote", content: [{ type: "paragraph", content: [{ type: "text", text: seed.quote }] }] },
  { type: "paragraph", content: [{ type: "text", text: "— " }, { type: "mention", attrs: { id: seed.mention.id, label: seed.mention.label, kind: seed.mention.kind, href: seed.mention.href } }] },
  { type: "paragraph" },
] });

/* Create or edit one journal entry. `entry` = existing entry (with doc) or null. */
export default function EntryComposer({ entry = null, questionId = null, seed = null, onSaved, onCancel, autoFocus = false, compact = false, showTitle = true }) {
  const [title, setTitle] = useState(entry?.title || "");
  const [doc, setDoc] = useState(entry?.doc || (seed ? seedDoc(seed) : EMPTY_DOC));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setTitle(entry?.title || ""); setDoc(entry?.doc || EMPTY_DOC); setDirty(false); }, [entry?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: reset the draft only when a different entry opens

  const save = async () => {
    if (isEmptyDoc(doc) && !title.trim()) { toast.info("Write something first."); return; }
    setSaving(true);
    try {
      const body = { title: title.trim(), doc, question_id: entry?.question_id ?? questionId ?? null };
      const { data } = entry ? await api.put(`/journal/${entry.id}`, body) : await api.post("/journal", body);
      toast.success(entry ? "Entry updated." : "Saved to your journal.");
      if (!entry) { setTitle(""); setDoc(EMPTY_DOC); }
      setDirty(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={compact ? "space-y-3" : "space-y-4"} data-testid="entry-composer">
      {showTitle && (
        <Input value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} placeholder="Title (optional)" data-testid="entry-title-input"
          className="h-auto border-0 border-b border-border bg-transparent px-0 font-serif text-2xl shadow-none focus-visible:ring-0 rounded-none" />
      )}
      <JournalEditor key={entry?.id || "new"} value={doc} onChange={(d) => { setDoc(d); setDirty(true); }} autoFocus={autoFocus} minHeight={compact ? 140 : 240} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground" data-testid="entry-private-note">Private. Only you can see your journal.</p>
        <div className="flex gap-2">
          {onCancel && <Button variant="ghost" className="rounded-full" onClick={onCancel} disabled={saving} data-testid="entry-cancel-button">Cancel</Button>}
          <Button onClick={save} disabled={saving || (entry && !dirty)} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="entry-save-button">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{entry ? "Update entry" : "Save entry"}
          </Button>
        </div>
      </div>
    </div>
  );
}
